import classNames from 'classnames/bind';
import { ReactElement } from 'react';
import { Pagination as SemanticUiPagination, PaginationProps as SemanticUiPaginationProps } from 'semantic-ui-react';
import Icon, { ICONS } from '../Icon';
import Tooltip from '../Tooltip';
import styles from './Pagination.module.scss';

const cx = classNames.bind(styles);

interface PaginationProps extends Omit<SemanticUiPaginationProps, 'onPageChange'> {
  activePage: number;
  onPageChange: (page: number) => void;
}

export enum PAGINATION_ITEM {
  FIRST = 'First',
  LAST = 'Last',
  PREVIOUS = 'Previous',
  NEXT = 'Next',
}

const paginationItemToIconMap = {
  [PAGINATION_ITEM.FIRST]: ICONS.CaretDoubleLeft,
  [PAGINATION_ITEM.LAST]: ICONS.CaretDoubleRight,
  [PAGINATION_ITEM.PREVIOUS]: ICONS.CaretLeft,
  [PAGINATION_ITEM.NEXT]: ICONS.CaretRight,
};

const Pagination = ({ activePage, totalPages, onPageChange, ...props }: PaginationProps): ReactElement => {
  const onPaginationPageChange = (e, data) => onPageChange(data.activePage);

  const renderIcon = (paginationItem: PAGINATION_ITEM) => (
    <Tooltip content={paginationItem}>
      <span className={cx('icon')} data-testid={`${paginationItem}_icon`}>
        <Icon name={paginationItemToIconMap[paginationItem]} />
      </span>
    </Tooltip>
  );

  const showFirstItems = activePage !== 1;
  const showLastItems = activePage !== totalPages;

  return (
    <div className={cx('body', 'no-print')}>
      <SemanticUiPagination
        firstItem={showFirstItems && { content: renderIcon(PAGINATION_ITEM.FIRST) }}
        prevItem={showFirstItems && { content: renderIcon(PAGINATION_ITEM.PREVIOUS) }}
        lastItem={null}
        nextItem={showLastItems && { content: renderIcon(PAGINATION_ITEM.NEXT) }}
        activePage={activePage}
        totalPages={totalPages}
        onPageChange={onPaginationPageChange}
        siblingRange={2}
        boundaryRange={0}
        {...props}
      />
    </div>
  );
};

export default Pagination;
